/**
 * @fileoverview Estilos do quadro compartilhado por link: a cara do CRM (fundo,
 * colunas e cards do Kanban), sem o menu nem nada do app logado. Neutro nos
 * dois temas; cor só na marca de "somente leitura" e nos avisos.
 */

import { makeStyles } from "@material-ui/core/styles";
import { modernFieldStyles } from "../../components/ModernSelect";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const textPrimary = dark ? "#E8EAED" : "#0F172A";
  const textSecondary = dark ? "#9CA3AF" : "#475569";
  const textMuted = dark ? "#6B7280" : "#64748B";
  const surface = dark ? "rgba(14,16,22,0.85)" : "#FFFFFF";
  const line = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)";
  const hover = dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)";
  const xs = theme.breakpoints.down("xs");

  return {
    root: {
      height: "100vh",
      "@supports (height: 100dvh)": { height: "100dvh" },
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      padding: theme.spacing(2.5, 3, 1.5),
      background: dark ? "#0A0A0A" : "#F8FAFC",
      color: textPrimary,
      [xs]: { padding: theme.spacing(1.5, 1.5, 1) },
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      flexShrink: 0,
    },
    headerLeft: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: "1 1 260px" },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    // O espaço da última letra (letter-spacing) sai, para o "·" ficar no meio.
    brand: { letterSpacing: "0.28em", marginRight: "-0.28em", color: dark ? "rgba(255,255,255,0.55)" : "#64748B" },
    title: {
      fontSize: "1.6rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.15,
      margin: 0,
      color: textPrimary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      [xs]: { fontSize: "1.25rem" },
    },
    headerRight: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    readOnlyBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      height: 32,
      padding: "0 12px",
      borderRadius: 999,
      fontSize: 12.5,
      fontWeight: 600,
      color: textSecondary,
      background: surface,
      border: `1px solid ${line}`,
      whiteSpace: "nowrap",
      "& svg": { flexShrink: 0 },
    },
    badgeLong: { [xs]: { display: "none" } },
    badgeShort: { display: "none", [xs]: { display: "inline" } },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: 4,
      padding: 3,
      borderRadius: 12,
      background: surface,
      border: `1px solid ${line}`,
    },
    iconBtn: {
      width: 32,
      height: 32,
      borderRadius: 9,
      padding: 0,
      color: textSecondary,
      "&:hover": { background: hover, color: textPrimary },
    },
    meta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "2px 10px",
      margin: "6px 0 14px",
      fontSize: 12.5,
      color: textMuted,
      flexShrink: 0,
      [xs]: { margin: "4px 0 10px" },
    },
    warning: { color: dark ? "#fbbf24" : "#b45309" },

    toolbar: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: theme.spacing(1.5),
      flexShrink: 0,
      flexWrap: "wrap",
      [xs]: { gap: 8, marginBottom: theme.spacing(1) },
    },
    searchWrap: { position: "relative", flex: "0 1 320px", minWidth: 200, [xs]: { flex: "1 1 100%" } },
    searchIcon: {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      color: textMuted,
      pointerEvents: "none",
      zIndex: 1,
    },
    searchField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: 38,
        borderRadius: 11,
        background: surface,
        border: `1px solid ${line}`,
        "& fieldset": { border: "none" },
        "&.Mui-focused": {
          borderColor: dark ? "rgba(99,102,241,0.42)" : "rgba(61,92,253,0.42)",
          boxShadow: dark ? "0 0 0 1px rgba(99,102,241,0.08)" : "0 0 0 1px rgba(61,92,253,0.08)",
        },
      },
      "& .MuiOutlinedInput-input": {
        padding: "0 12px 0 36px",
        fontSize: 13,
        height: 38,
        color: textPrimary,
        "&::placeholder": { color: textMuted, opacity: 1 },
      },
    },
    assigneeSelect: { minWidth: 220, [xs]: { flex: "1 1 100%" }, "& .MuiInputBase-root": { height: 38, fontSize: 13 } },
    notice: { fontSize: 12.5, color: textMuted, margin: "0 0 10px", flexShrink: 0 },

    boardWrapper: { flex: 1, display: "flex", overflow: "hidden", minHeight: 0 },
    board: {
      display: "flex",
      gap: 12,
      overflowX: "auto",
      overflowY: "hidden",
      flex: 1,
      padding: "4px 4px 12px 2px",
      alignItems: "flex-start",
      WebkitOverflowScrolling: "touch",
      "&::-webkit-scrollbar": { height: 10 },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
        borderRadius: 5,
      },
    },

    // Telas de estado e a senha: um cartão no meio da tela.
    center: {
      minHeight: "100vh",
      "@supports (height: 100dvh)": { minHeight: "100dvh" },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 18,
      padding: 16,
      background: dark ? "#0A0A0A" : "#F8FAFC",
    },
    panel: {
      width: "100%",
      maxWidth: 400,
      borderRadius: 18,
      padding: "28px 24px 24px",
      background: dark ? "#1c1e23" : "#ffffff",
      boxShadow: dark
        ? "0 24px 48px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)"
        : "0 24px 48px -12px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 10,
      [xs]: { padding: "24px 18px 20px" },
    },
    panelIcon: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: hover,
      color: textSecondary,
      marginBottom: 4,
    },
    panelTitle: { fontSize: 18, fontWeight: 600, margin: 0, color: textPrimary },
    panelText: { fontSize: 14, lineHeight: 1.55, margin: 0, color: textSecondary },
    panelForm: { width: "100%", display: "flex", flexDirection: "column", gap: 12, marginTop: 8 },
    field: { ...modernFieldStyles(theme), "& .MuiInputBase-root": { fontSize: 14 } },
    panelError: { fontSize: 13, margin: 0, color: dark ? "#fca5a5" : "#b91c1c", textAlign: "left" },
    panelNote: { fontSize: 13, margin: 0, color: dark ? "#fbbf24" : "#b45309", lineHeight: 1.5 },
    primaryBtn: {
      height: 42,
      borderRadius: 11,
      border: "none",
      fontSize: 14.5,
      fontWeight: 600,
      fontFamily: "inherit",
      cursor: "pointer",
      color: "#ffffff",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      "&:hover:not(:disabled)": { background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)" },
      "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
      "&:focus-visible": { outline: "2px solid #93b4ff", outlineOffset: 2 },
    },
    ghostBtn: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)"}`,
      background: "transparent",
      color: textSecondary,
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "inherit",
      cursor: "pointer",
      "&:hover": { background: hover, color: textPrimary },
    },
    footerBrand: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.28em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
    },

    // Esqueleto enquanto o quadro carrega.
    skeletonColumns: { display: "flex", gap: 12, overflow: "hidden", flex: 1, paddingTop: 8 },
    skeletonColumn: {
      width: 296,
      minWidth: 296,
      borderRadius: 12,
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      background: surface,
      border: `1px solid ${line}`,
      alignSelf: "flex-start",
    },
    skeleton: { background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)", borderRadius: 8 },
    srOnly: {
      position: "absolute",
      width: 1,
      height: 1,
      overflow: "hidden",
      clip: "rect(0 0 0 0)",
      whiteSpace: "nowrap",
    },
  };
});

export default useStyles;
